<script setup lang="ts">
import { modelMapper } from '~~/models'
import type { AppGame } from '~~/models/appGame'

useTitle('apps.title', { t: true })

interface State {
  apps: AppGame[],
  search: string
}

const state: State = reactive({
  apps: [],
  search: ''
})

const apiClient = useAPIClient()
const data = await apiClient.fetch('apps', { headers: { 'User-Agent': 'pz-app-v3.1.0' } })
state.apps = (data as any[]).map(d => modelMapper.App(d))

const results = computed(() => {
  if (!state.search) return state.apps

  return state.apps.filter(a => a.name.toLowerCase().includes(state.search.toLowerCase()))
})

const games = computed(() => results.value.filter(a => a.kind === 'game'))
const apps = computed(() => results.value.filter(a => a.kind === 'app'))
</script>

<template>
  <div class="page">
    <header>
      <h1>{{ $t('apps.title') }}</h1>

      <div class="description">
        <p>
          {{ $t('apps.description') }}
        </p>

        <p>
          <i18n-t keypath="apps.app_not_listed">
            <template v-slot:contact>
              <NuxtLinkLocale href="/support">
                {{ $t('apps.contact_us') }}
              </NuxtLinkLocale>
            </template>
          </i18n-t>
        </p>
      </div>

      <div class="content-limit">
        <FormKit type="search" outer-class="app-search" input-class="styled" :placeholder="$t('common.search')"
          v-model="state.search" />
      </div>
    </header>

    <div v-if="games.length" class="games">
      <ul class="app-list">
        <li v-for="game of games" :key="game.id">
          <AppEntry :app="game" />
        </li>
      </ul>
    </div>

    <div v-if="apps.length" class="apps">
      <div class="header gaming-apps">
        <h2>{{ $t('apps.apps_title') }}</h2>
      </div>

      <ul class="app-list">
        <li v-for="app of apps" :key="app.id">
          <AppEntry :app="app" />
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page {
  background-color: $color-royal;
  color: $color-white;
}

header {
  text-align: center;
  margin-bottom: 2rem;

  :deep(input) {
    width: 100%;
    @media (min-width: $breakpoint-sm) {
      width: 35%;
    }
  }
}

.description {
  margin-bottom: 2rem;

  @media (min-width: $breakpoint-md) {
    width: 75%;
    margin-left: auto;
    margin-right: auto;
  }
}

.app-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 1rem;
  column-gap: 1rem;
  
  @include unstyled-list;

  @media (min-width: $breakpoint-sm) {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.gaming-apps {
  margin-top: 2rem;
}
</style>
